<template>
  <div class="rating" :class="{ 'rating--block': kind !== 'leg' }">
    <v-chip :color="color" :variant="variant" size="small" label class="font-mono">
      {{ headlineText }}
    </v-chip>
    <span v-if="meaningText" class="rating-meaning text-caption">{{ meaningText }}</span>
    <span v-if="detailText" class="rating-detail text-caption text-medium-emphasis">
      {{ detailText }}
    </span>
  </div>
</template>

<script>
// Traffic-light colours, deliberately the same three the punctuality pages use.
const VERDICT_COLORS = {
  GOOD: 'success',
  FAIR: 'warning',
  POOR: 'error',
};

export default {
  name: 'ConnectionRating',

  props: {
    rating: { type: Object, default: null },
    kind: { type: String, default: 'connection' },
  },

  computed: {
    unrated() {
      return !this.rating || this.rating.coverage === 'NONE';
    },

    // Some of the journey had history and some had none. The backend never infers this away
    // (see its Coverage enum), so neither do we: the number below is real, but it does not
    // stand for the whole trip.
    partial() {
      return Boolean(this.rating) && this.rating.coverage === 'PARTIAL';
    },

    // Only the connection is coloured, and only from the verdict the server computed.
    //
    // A verdict is more than its percentage — the server additionally requires the median
    // delay to sit inside a configured bound before it calls a rate GOOD. Colouring legs and
    // transfers here from a frontend copy of the server's 0.8/0.6 thresholds therefore put an
    // orange headline chip and a green leg chip on the identical 82 % one click apart. The
    // payload carries no per-leg verdict to borrow instead, so legs and transfers stay
    // neutral and let their number speak.
    color() {
      if (this.unrated || this.kind !== 'connection') {
        return undefined;
      }
      return VERDICT_COLORS[this.rating.verdict];
    },

    variant() {
      if (!this.color) {
        return 'tonal';
      }
      // Outlined instead of filled marks a partly covered rating without shouting.
      return this.partial ? 'outlined' : 'flat';
    },

    // The one number this badge leads with, per level.
    rate() {
      if (!this.rating) {
        return null;
      }
      if (this.kind === 'connection') {
        return this.rating.headline ? this.rating.headline.rate : null;
      }
      if (this.kind === 'transfer') {
        return this.rating.successRate;
      }
      return this.rating.punctualRate;
    },

    sampleDays() {
      if (!this.rating) {
        return 0;
      }
      if (this.kind === 'connection') {
        return this.rating.headline ? this.rating.headline.sampleDays : this.rating.sampleDays || 0;
      }
      return this.rating.sampleDays || 0;
    },

    headlineText() {
      if (this.unrated) {
        // "keine Daten" is a claim about the whole journey and is reserved for it. A single
        // unrated leg or transfer means we have nothing to compare *it* against — which happens
        // routinely for a train that only starts serving a station under a future timetable, and
        // says nothing about the legs beside it, which may well carry a full month of history.
        return this.kind === 'connection' ? 'keine Daten' : 'keine Vergleichstage';
      }
      if (this.rate == null) {
        return 'nicht bewertbar';
      }
      return `${Math.round(this.rate * 100)} %`;
    },

    detailText() {
      if (this.unrated) {
        return this.kind === 'connection' ? 'für diese Strecke liegt keine Historie vor' : null;
      }
      if (this.rate == null) {
        // Two different reasons for the same missing number, and only `coverage` tells them
        // apart: PARTIAL means a part of the journey has no history at all, FULL means every
        // part had history and there were simply fewer comparable days than the backend needs
        // before it will call a percentage meaningful.
        return this.partial
          ? 'ein Abschnitt ohne Historie'
          : 'zu wenige vergleichbare Tage im Zeitraum';
      }
      return this.partial ? `${this.countText} · teilweise bewertet` : this.countText;
    },

    // "X von Y Tagen", in the wording that fits the level. successDays is documented as exactly
    // the headline's numerator, so it is read rather than recomputed; a leg has no such field and
    // its model's javadoc blesses the multiplication.
    //
    // The connection wording no longer branches: the backend's headline means one thing now —
    // the days the whole journey held together — for a direct train and a four-change trip alike.
    countText() {
      if (this.kind === 'transfer') {
        return `an ${this.rating.successDays} von ${this.sampleDays} Tagen geschafft`;
      }
      if (this.kind === 'connection') {
        return `an ${this.rating.successDays} von ${this.sampleDays} Tagen`;
      }
      return `pünktlich an ${Math.round(this.rate * this.sampleDays)} von ${this.sampleDays} Tagen`;
    },

    // What the percentage actually measures, said out loud next to it. Without this the number is
    // just "82 %" of something the reader has to guess at — the complaint that prompted the change.
    meaningText() {
      if (this.unrated || this.rate == null) {
        return null;
      }
      if (this.kind === 'transfer') {
        return 'Umstieg hat geklappt';
      }
      if (this.kind === 'connection') {
        return 'Reise wie geplant geschafft';
      }
      return null;
    },
  },
};
</script>

<style scoped>
.rating {
  display: flex;
  align-items: center;
  gap: 8px;
}
.rating--block {
  flex-wrap: wrap;
}
.rating-detail {
  white-space: nowrap;
}
.rating-meaning {
  white-space: nowrap;
}
</style>
